import React, { ReactNode, useCallback, useMemo, useReducer } from 'react';
import { useWeb3React } from '@web3-react/core';
import AppContext from './appContext';
import appReducer from './appReducer';
import {
    SpoolAllocationInputDto,
    SpoolDto,
    SpoolSdk,
    SpoolStrategyDto,
    SpoolUiDomain,
} from '../../types/spool-local';
import {
    ConfigAddressConstant,
    RiskModelConstant,
    StrategiesConstant,
    StrategyApyRisks,
} from './appTypes';
// import { SDK_CONFIG_ETHEREUM, SDK_CONFIG_ARBITRUM } from '../../config/config';
import { EmissionSpoolDto } from '../emissionSpools/emissionSpoolsType';
import { SupportedChains } from '../../config/constants';
import ETHEREUM_STRATEGIES from '../../config/constants/ethereumStrategies.json';
import ARBITRUM_STRATEGIES from '../../config/constants/arbitrumStrategies.json';
import ETHEREUM_RISK_MODELS from '../../config/constants/ethereumRiskModels.json';
import ARBITRUM_RISK_MODELS from '../../config/constants/arbitrumRiskModels.json';
import { config } from '../../config/config';

const AppState: React.FC<ReactNode> = ({ children }) => {
    const { chainId } = useWeb3React();

    const sdk = undefined as unknown as SpoolSdk;
    const interfaceSDK = undefined as unknown as SpoolUiDomain;
    // const sdk = useMemo(
    //     () =>
    //         library && chainId === SupportedChains.ARBITRUM_ONE
    //             ? new SpoolSdk(
    //                   account ? library.getSigner() : library,
    //                   SDK_CONFIG_ARBITRUM as unknown as SpoolSdkParameters
    //               )
    //             : library
    //             ? new SpoolSdk(
    //                   account ? library.getSigner() : library,
    //                   SDK_CONFIG_ETHEREUM as unknown as SpoolSdkParameters
    //               )
    //             : undefined,
    //     [library, account, chainId]
    // );

    // const interfaceSDK = useMemo(
    //     () =>
    //         library && chainId === SupportedChains.ARBITRUM_ONE
    //             ? new SpoolUiDomain(
    //                   account ? library.getSigner() : library,
    //                   SDK_CONFIG_ARBITRUM as unknown as SpoolUiDomainParameters
    //               )
    //             : library
    //             ? new SpoolUiDomain(
    //                   account ? library.getSigner() : library,
    //                   SDK_CONFIG_ETHEREUM as unknown as SpoolUiDomainParameters
    //               )
    //             : undefined,
    //     [library, account, chainId]
    // );

    const chainStrategies = useMemo(
        () =>
            chainId === SupportedChains.ARBITRUM_ONE
                ? (ARBITRUM_STRATEGIES as StrategiesConstant)
                : (ETHEREUM_STRATEGIES as StrategiesConstant),
        [chainId]
    );

    const chainRiskModels = useMemo(
        () =>
            chainId === SupportedChains.ARBITRUM_ONE
                ? (ARBITRUM_RISK_MODELS as RiskModelConstant)
                : (ETHEREUM_RISK_MODELS as RiskModelConstant),
        [chainId]
    );

    const chainConfig = useMemo(
        () =>
            chainId === SupportedChains.ARBITRUM_ONE
                ? (config.arbitrumSpoolAddresses as ConfigAddressConstant)
                : (config.ethereumSpoolAddresses as ConfigAddressConstant),
        [chainId]
    );

    const setIsLoading = (loadingKey: string, loadingState: boolean) =>
        dispatch({
            type: 'SET_LOADING',
            payload: {
                loadingKey,
                loadingState,
            },
        });

    const setSelectedSpool = useCallback(
        (spool: SpoolDto): void =>
            dispatch({
                type: 'SET_SELECTED_SPOOL',
                payload: spool,
            }),
        []
    );

    const setSelectedEmissionSpool = useCallback(
        (spool: EmissionSpoolDto): void =>
            dispatch({
                type: 'SET_SELECTED_EMISSION_SPOOL',
                payload: spool,
            }),
        []
    );

    const setCurrentViewingModel = useCallback(
        (model: string): void =>
            dispatch({
                type: 'SET_CURRENT_VIEWING_MODEL',
                payload: model,
            }),
        []
    );

    const getRiskModels = useCallback(
        async () => {
            try {
                setIsLoading('riskModels', true);

                // const models = await sdk!.spools.getRiskModels();

                dispatch({
                    type: 'SET_RISK_MODELS',
                    payload: {
                        '0xc216ad6280f4fa92a5159ef383a1206d432481c8': 5,
                        '0x9272440fc203115d99c9fd3517a6dd6eeb4224e9': 10,
                    },
                });
            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading('riskModels', false);
            }
        },
        [
            // sdk
        ]
    );

    const getStrategies = useCallback(
        async (assetAddress: string, riskModelId: string) => {
            try {
                setIsLoading('strategies', true);

                // const strats = await sdk!.spools.getStrategies(riskModelId, assetAddress);

                dispatch({
                    type: 'SET_STRATEGIES',
                    payload: [
                        '0xf36d05c1b7810013334ce6f1986aaefde1eb34fc',
                        '0xc7c5652874de32a5ce305bd9195b7c665281648e',
                        '0x9e9d4d747a3dfe3d135077cfa1baf98599cf5b80',
                        '0x335bd363fe258f0af80a2b664c1c9b45ab6fce9d',
                        '0x7957901a8941fad236483eaa0332880c2f507654',
                        '0x87581d026eb25269e3ebb61a47025892c6a1e79c',
                        '0x0e3f4f0e9c6ac6dbd8f662d5874dec0c2aae727e',
                        '0xb1f298e91cfcae8f7f8bd98b19f3d0868a47b254',
                        '0x0b7f42dc09a48d2d40f0226a79be86c71da44ba6',
                        '0x2394d04f0071397d799d9ed392e631ac2499fed7',
                        '0x099f65906d09fea4628d9f0b5e0637293a79e28b',
                        '0x31c2f932052f7861ac3f301c584d6888b6e6896d',
                        '0x87f0c2b4659a79a9bbe1bfc3889eaba55ac0350d',
                        '0xbfa7646f4a20cc303501ab7c9ee4416401ccd532',
                    ],
                });
            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading('strategies', false);
            }
        },
        [
            // sdk
        ]
    );

    const getStrategyAllocationDetails = useCallback(
        async (
            strategies: SpoolStrategyDto[],
            riskModelId: string
        ): Promise<StrategyApyRisks | undefined> => {
            return undefined;

            // try {
            //     const strategyIds = strategies.map(({ id }) => id.split('-')[1]);
            //     const allocations = strategies.reduce<{ [x: string]: string }>(
            //         (acc, { id, allocation }) => ({
            //             ...acc,
            //             [id.split('-')[1]]: allocation,
            //         }),
            //         {}
            //     );

            //     const apys = await sdk!.spools.getStrategyApys(strategyIds);
            //     const risks = await sdk!.spools.getStrategyRisks(
            //         strategyIds,
            //         riskModelId
            //     );

            //     if (!apys || !risks || !allocations) {
            //         return;
            //     }

            //     const merge = Object.entries(apys).reduce(
            //         (acc, [strategyAddress, apy]) => ({
            //             ...acc,
            //             [strategyAddress.toLowerCase()]: {
            //                 apy,
            //                 risk: risks[strategyAddress.toLowerCase()],
            //                 allocation: allocations[strategyAddress.toLowerCase()],
            //             },
            //         }),
            //         {}
            //     );

            //     return merge;
            // } catch (error) {
            //     console.log(error);
            // }
        },
        [
            // sdk
        ]
    );

    // const getSpoolsByRiskModel = useCallback(
    //     async (riskModelAddress: string) => {
    //         try {
    //             const spools = await sdk!.get.getSpoolsByRiskModel(riskModelAddress);
    //             console.log(spools);
    //         } catch (error) {
    //             console.log(error);
    //         }
    //     },
    //     [sdk]
    // );

    const getEstimatedAllocationDetails = useCallback(
        async (params: SpoolAllocationInputDto) => {
            // try {
            //     setIsLoading('estimatedAllocations', true);
            //     const allocations = await sdk!.spools.getStrategyAllocations(params);
            //     dispatch({
            //         type: 'SET_ESTIMATED_ALLOCATIONS',
            //         payload: allocations,
            //     });
            // } catch (error) {
            //     console.log(error);
            // } finally {
            //     setIsLoading('estimatedAllocations', false);
            // }
        },
        [
            // sdk
        ]
    );

    const initialState = {
        sdk,
        interfaceSDK,
        chainStrategies,
        chainRiskModels,
        chainConfig,
        riskModels: null,
        strategies: null,
        estimatedAllocations: null,
        isLoading: {
            spools: false,
            riskModels: false,
            strategies: false,
            estimatedAllocations: false,
        },
        selectedSpool: null,
        selectedEmissionSpool: null,
        currentViewingRiskModel: null,
        setIsLoading,
        setCurrentViewingModel,
        getRiskModels,
        getStrategies,
        getStrategyAllocationDetails,
        getEstimatedAllocationDetails,
        setSelectedSpool,
        setSelectedEmissionSpool,
    };

    const [state, dispatch] = useReducer(appReducer, initialState);

    return (
        <AppContext.Provider
            value={{
                sdk,
                interfaceSDK,
                chainStrategies,
                chainRiskModels,
                chainConfig,
                riskModels: state.riskModels,
                strategies: state.strategies,
                estimatedAllocations: state.estimatedAllocations,
                isLoading: state.isLoading,
                selectedSpool: state.selectedSpool,
                currentViewingRiskModel: state.currentViewingRiskModel,
                selectedEmissionSpool: state.selectedEmissionSpool,
                setIsLoading,
                getRiskModels,
                getStrategies,
                getStrategyAllocationDetails,
                getEstimatedAllocationDetails,
                setSelectedSpool,
                setSelectedEmissionSpool,
                setCurrentViewingModel,
            }}
        >
            {children}
        </AppContext.Provider>
    );
};

export default AppState;
